import { k } from '../../localization/t';
import { ALCHEMIST_NODES } from './alchemist';
import { REALMWALKER_NODES } from './realmWalker';

// The six classes. A player chooses exactly one before spending any
// skill point, and every point goes into that class's tree. Changing
// class is a paid reset (see skills.js).
//
// Each tree is a constellation: nodes carry a position in a 1000×600
// design box (x right, y down) and are drawn as stars, prerequisites
// as lines between them. Nothing about the layout is a grid; the
// positions are authored by hand to read as a sign in the sky.
//
// Node fields:
//   key      unique across all classes, prefixed with the class key
//   title    player-facing name (k() marked, rendered through t())
//   desc     player-facing effect text
//   type     'gameplay' | 'rewards' | 'hub'. Board vocabulary.
//   major    the three mechanic-introducing nodes of a class
//   minLevel level gate, majors only
//   requires node keys of which ANY ONE must be purchased first
//   excludes node keys that cannot be held together (either-or forks)
//   x, y     constellation position
//
// Classes without a tree yet carry a `sigil`: a small constellation
// drawn on the picker so the sky reads complete before their trees
// exist. Sigil coordinates share the 1000×600 box.
//
// The server mirrors the structure in valkenhall-server/src/utils/classTrees.js
// and validates every spend against it. Keep the two in lockstep.

const WILDHEART_NODES = [
  { key: 'wildheart_oath', title: k('Oath of Blood and Briar'), type: 'hub', major: true, x: 500, y: 548,
    desc: k('Enemy minions you slay drop loot by family: Fangs, Insignias or Essences. Opens the Hall of Antlers and the three altars, one offering each.') },

  // The Hunt
  { key: 'wildheart_tally', title: k('The Long Tally'), type: 'rewards', requires: ['wildheart_oath'], x: 336, y: 478,
    desc: k('Slain minions drop 15% more loot.') },
  { key: 'wildheart_apex', title: k('Apex Trophy'), type: 'rewards', requires: ['wildheart_tally'], x: 268, y: 392,
    desc: k('Elite and Unique kills drop 50% more loot.') },
  { key: 'wildheart_brand', title: k('Brand of the Hunt'), type: 'gameplay', major: true, minLevel: 10, requires: ['wildheart_apex'], x: 226, y: 292,
    desc: k('Before each match, brand one family as your prey. Slain branded minions drop double loot.') },
  { key: 'wildheart_feud', title: k('Blood Feud'), type: 'gameplay', requires: ['wildheart_brand'], x: 132, y: 222,
    desc: k('Brand two families instead of one.') },
  { key: 'wildheart_grudge', title: k('Old Grudge'), type: 'rewards', requires: ['wildheart_feud'], x: 84, y: 128,
    desc: k('If no branded prey fell, the brand carries into your next match with +1x loot. Stacks up to 4x.') },
  { key: 'wildheart_scent', title: k('Scent of the Quarry'), type: 'gameplay', requires: ['wildheart_brand'], excludes: ['wildheart_ashen'], x: 268, y: 192,
    desc: k("Choose your brand when the opponent's first minion enters play, instead of before the match.") },
  { key: 'wildheart_ashen', title: k('The Ashen Brand'), type: 'gameplay', requires: ['wildheart_brand'], excludes: ['wildheart_scent'], x: 356, y: 126,
    desc: k('Branded kills drop 4x loot. Unbranded kills drop nothing.') },

  // The Altars
  { key: 'wildheart_tribute', title: k('Second Tribute'), type: 'hub', requires: ['wildheart_oath'], x: 508, y: 446,
    desc: k("Pay 15 loot to re-roll an altar's offerings. Once per altar per day.") },
  { key: 'wildheart_freyr', title: k("Freyr's Bounty"), type: 'hub', requires: ['wildheart_tribute'], x: 412, y: 352,
    desc: k('Freyr may offer Gilded Boosters and bundles of three. Every roll has a small chance to show a booster display.') },
  { key: 'wildheart_odin', title: k('Whispers in the Wyrd'), type: 'hub', requires: ['wildheart_tribute'], x: 502, y: 326,
    desc: k("Odin may offer the ranked season's alt-art foil cards and foil avatars.") },
  { key: 'wildheart_tyr', title: k('Raiment of the Vanquished'), type: 'hub', requires: ['wildheart_tribute'], x: 594, y: 350,
    desc: k('Tyr adds playmats and dice to his portraits and backgrounds.') },
  { key: 'wildheart_abundance', title: k('Abundance'), type: 'hub', requires: ['wildheart_freyr', 'wildheart_odin', 'wildheart_tyr'], x: 506, y: 236,
    desc: k('Every altar gains a second slot. It is always veiled: you see the tier and a 30% lower price, never the item.') },
  { key: 'wildheart_bones', title: k('Reading the Bones'), type: 'hub', requires: ['wildheart_abundance'], x: 612, y: 198,
    desc: k('Veiled offerings roll one tier higher 25% of the time.') },
  { key: 'wildheart_culling', title: k('Culling of the Weak'), type: 'hub', major: true, minLevel: 20, requires: ['wildheart_abundance'], x: 500, y: 128,
    desc: k("Sacrifice one of an altar's two offerings to gild the other: a single becomes foil, each booster gains one guaranteed foil, a cosmetic rises one tier. The price stays fixed.") },
  { key: 'wildheart_thorns', title: k('Crown of Thorns'), type: 'hub', requires: ['wildheart_culling'], excludes: ['wildheart_veil'], x: 428, y: 52,
    desc: k('A sacrificed offering refunds half its price in loot.') },
  { key: 'wildheart_veil', title: k('Under the Veil'), type: 'hub', requires: ['wildheart_culling'], excludes: ['wildheart_thorns'], x: 578, y: 56,
    desc: k('You may lift a veiled stone and see what lies beneath before you buy. A lifted stone costs 20% more.') },

  // The Field
  { key: 'wildheart_carrion', title: k('Carrion Rights'), type: 'rewards', requires: ['wildheart_oath'], x: 684, y: 478,
    desc: k('Lost matches pay 50% more.') },
  { key: 'wildheart_spoils', title: k('Beast Tamer'), type: 'rewards', requires: ['wildheart_carrion'], x: 766, y: 396,
    desc: k('After a won match, 50% chance that one slain enemy beast joins your card backs collection.') },
  { key: 'wildheart_antlers', title: k('Antlers Above the Hearth'), type: 'hub', requires: ['wildheart_spoils'], x: 836, y: 300,
    desc: k("Slaying 25, 50 and 100 species of a family pays a loot bundle. At 100 you also earn that family's card back.") },
];

// Battlemage, from class-design.excalidraw. Drawn as a blade lying on its
// side: the hilt (Rush of Battle) at the left, the point at the right.
const BATTLEMAGE_NODES = [
  { key: 'battlemage_rush', title: k('Rush of Battle'), type: 'rewards', major: true, x: 60, y: 300,
    desc: k('Winning matches in a row grants Rush of Battle stacks, up to 3. Each stack pays 10% more Gold, Arcana and XP.') },
  { key: 'battlemage_resolve', title: k('Resolve'), type: 'rewards', requires: ['battlemage_rush'], x: 60, y: 150,
    desc: k('Each Rush of Battle stack pays 5% more.') },
  { key: 'battlemage_march', title: k('March Onwards'), type: 'rewards', requires: ['battlemage_rush'], x: 60, y: 450,
    desc: k('Raise your Rush of Battle stack limit by 1.') },
  { key: 'battlemage_conquest', title: k('The Conquest Continues'), type: 'rewards', requires: ['battlemage_resolve'], x: 180, y: 150,
    desc: k('Rush of Battle stacks without limit, but all your match rewards are reduced by 50%.') },
  { key: 'battlemage_bask', title: k('Bask in Glory'), type: 'rewards', requires: ['battlemage_rush'], x: 180, y: 300,
    desc: k('At 3 Rush of Battle stacks, match rewards rise by a further 25%.') },
  { key: 'battlemage_overkill', title: k('Overkill'), type: 'rewards', requires: ['battlemage_march'], x: 180, y: 450,
    desc: k('At maximum Rush of Battle stacks, rewards rise by a further 150%, but a victory then spends every stack.') },
  { key: 'battlemage_approach', title: k('Immanent Approach'), type: 'rewards', requires: ['battlemage_bask'], x: 290, y: 300,
    desc: k('Raise your Rush of Battle stack limit by 1.') },
  { key: 'battlemage_bloodshed', title: k('Bloodshed'), type: 'gameplay', major: true, minLevel: 10, requires: ['battlemage_approach'], x: 410, y: 300,
    desc: k('Damage dealt to the enemy avatar grants Ferocity. Spend 100 Ferocity to keep your Rush of Battle stacks through a defeat.') },
  { key: 'battlemage_thrill', title: k('Thrill of Battle'), type: 'rewards', requires: ['battlemage_bloodshed'], x: 590, y: 180,
    desc: k('At maximum Ferocity, each Rush of Battle stack pays 10% more.') },
  { key: 'battlemage_flawless', title: k('Flawless Victory'), type: 'gameplay', requires: ['battlemage_bloodshed'], x: 590, y: 420,
    desc: k('Take no damage in a match to earn double Ferocity from it.') },
  { key: 'battlemage_all', title: k('All or Nothing'), type: 'rewards', requires: ['battlemage_thrill'], x: 590, y: 60,
    desc: k('At maximum Ferocity, each Rush of Battle stack pays 15% more. Ferocity can no longer be spent to keep stacks through a defeat.') },
  { key: 'battlemage_overflow', title: k('Overflow'), type: 'rewards', requires: ['battlemage_flawless'], x: 590, y: 540,
    desc: k('Ferocity earned beyond your maximum is paid out as Gold.') },
  { key: 'battlemage_uprise', title: k('Uprise'), type: 'gameplay', requires: ['battlemage_thrill', 'battlemage_flawless'], excludes: ['battlemage_point'], x: 635, y: 300,
    desc: k('Maximum Ferocity is increased by 30.') },
  { key: 'battlemage_point', title: k('Point of No Return'), type: 'gameplay', requires: ['battlemage_thrill', 'battlemage_flawless'], excludes: ['battlemage_uprise'], x: 545, y: 300,
    desc: k('Maximum Ferocity is reduced by 50.') },
  { key: 'battlemage_glory', title: k('The Glory'), type: 'hub', major: true, minLevel: 20, requires: ['battlemage_thrill', 'battlemage_flawless'], x: 755, y: 300,
    desc: k('Spend Ferocity to re-roll Arcane Trial quests and foil cards.') },
  { key: 'battlemage_arsenal', title: k('Expanded Arsenal'), type: 'hub', requires: ['battlemage_glory'], x: 830, y: 190,
    desc: k('Re-rolled foil cards are never duplicates of cards you already own.') },
  { key: 'battlemage_request', title: k('An Unexpected Request'), type: 'hub', requires: ['battlemage_glory'], x: 830, y: 410,
    desc: k('Re-rolled quests may offer double rewards.') },
  { key: 'battlemage_trial', title: k('Trial by Fire'), type: 'hub', requires: ['battlemage_arsenal'], x: 950, y: 190,
    desc: k('Arcane Trial track rewards also grant Ferocity.') },
  { key: 'battlemage_momentum', title: k('New-found Momentum'), type: 'hub', requires: ['battlemage_request'], x: 950, y: 410,
    desc: k('Re-rolled quests may grant Rush of Battle stacks as rewards.') },
];

// Oracle, from class-design.excalidraw. Drawn as an eye: the two
// foundations are the lids' peaks, top and bottom, and the Foresight
// line runs across the middle from the Omen of Insight to Unique Sight.
const ORACLE_NODES = [
  { key: 'oracle_peek', title: k('Peek into the Future'), type: 'rewards', major: true, x: 465, y: 60,
    desc: k('Before each match, predict your victory. A correct prediction doubles your rewards and grants a Foresight point. A wrong one halves them.') },
  { key: 'oracle_branching', title: k('Branching Paths'), type: 'gameplay', major: true, x: 465, y: 540,
    desc: k('Before each match, choose one of three Omens to carry into it. Complete the Omen to claim its reward.') },

  // Predictions, from the upper lid
  { key: 'oracle_close', title: k('That Was Close'), type: 'rewards', requires: ['oracle_peek'], x: 305, y: 135,
    desc: k('A failed prediction has a 20% chance to cost you nothing.') },
  { key: 'oracle_point', title: k('Point of No Return'), type: 'rewards', requires: ['oracle_close'], x: 165, y: 78,
    desc: k('Successful predictions grant one extra Foresight point.') },
  { key: 'oracle_mind', title: k('Mind over Matter'), type: 'rewards', requires: ['oracle_peek'], x: 600, y: 135,
    desc: k('Successful predictions pay extra Gold and Arcana equal to your Foresight.') },
  { key: 'oracle_expanse_mind', title: k('Vast Expanse'), type: 'rewards', requires: ['oracle_mind'], x: 710, y: 60,
    desc: k('Maximum Foresight is increased by 5.') },

  // Omens, from the lower lid
  { key: 'oracle_better', title: k('There Must Be a Better Way'), type: 'gameplay', requires: ['oracle_branching'], x: 305, y: 470,
    desc: k('Spend Foresight to re-roll your Omens.') },
  { key: 'oracle_ideal', title: k('An Ideal Outcome'), type: 'gameplay', requires: ['oracle_better'], x: 165, y: 522,
    desc: k('Omens may appear at a higher rarity, harder to complete but richer in reward.') },
  { key: 'oracle_horizon', title: k('Below the Horizon'), type: 'rewards', requires: ['oracle_branching'], x: 600, y: 470,
    desc: k('Predictions made with no Foresight carry no penalty.') },
  { key: 'oracle_expanse_horizon', title: k('Vast Expanse'), type: 'rewards', requires: ['oracle_horizon'], x: 710, y: 540,
    desc: k('Maximum Foresight is increased by 5.') },

  // The line of sight
  { key: 'oracle_written', title: k('Written in the Cards'), type: 'rewards', requires: ['oracle_point', 'oracle_ideal'], x: 165, y: 300,
    desc: k('Predictions and Omens may reward random cards.') },
  { key: 'oracle_insight', title: k('Omen of Insight'), type: 'gameplay', requires: ['oracle_written'], x: 60, y: 300,
    desc: k('Adds the Omen of Insight to your pool: win a match without taking damage to fully restore your Foresight.') },
  { key: 'oracle_expanse_written', title: k('Vast Expanse'), type: 'rewards', requires: ['oracle_written'], x: 305, y: 300,
    desc: k('Maximum Foresight is increased by 10.') },
  { key: 'oracle_akashicality', title: k('Akashicality'), type: 'rewards', requires: ['oracle_expanse_written'], x: 380, y: 300,
    desc: k('Maximum Foresight is raised to 500, and Foresight earned is doubled.') },
  { key: 'oracle_expanse_centre', title: k('Vast Expanse'), type: 'rewards', requires: ['oracle_peek', 'oracle_branching'], x: 465, y: 300,
    desc: k('Maximum Foresight is increased by 5.') },
  { key: 'oracle_look', title: k('Look Deeper'), type: 'hub', major: true, minLevel: 10, requires: ['oracle_expanse_centre', 'oracle_mind', 'oracle_horizon'], x: 600, y: 300,
    desc: k('Spend Foresight to re-roll ordinary cards in packs.') },
  { key: 'oracle_exceptional', title: k('Exceptional Sight'), type: 'hub', requires: ['oracle_look'], x: 710, y: 300,
    desc: k('Spend Foresight to re-roll Exceptional cards in packs.') },
  { key: 'oracle_elite', title: k('Elite Sight'), type: 'hub', requires: ['oracle_exceptional'], x: 790, y: 300,
    desc: k('Spend Foresight to re-roll Elite cards in packs.') },
  { key: 'oracle_unique', title: k('Unique Sight'), type: 'hub', requires: ['oracle_elite'], x: 870, y: 300,
    desc: k('Spend Foresight to re-roll Unique cards in packs.') },
  { key: 'oracle_expanse_unique', title: k('Vast Expanse'), type: 'rewards', requires: ['oracle_unique'], x: 940, y: 300,
    desc: k('Maximum Foresight is increased by 5.') },
];

export const CLASSES = [
  {
    key: 'wildheart',
    title: k('Wildheart'),
    tagline: k('Nothing dies. It only changes skin.'),
    accent: '#8fd3a4',
    // Faint figure behind the sign on the tree screen: a beast knot.
    art: '/ornaments/viking-ringerike-004.svg',
    // Medallion ornaments: the shared Viking tile on minor nodes, the
    // elk rune wheel on the three majors.
    ornament: '/ornaments/viking-style2d-007.svg',
    majorOrnament: '/ornaments/path-hunter.svg',
    summary: [
      k('Every enemy minion you slay leaves loot behind, kept by family in your trophy hall.'),
      k('Bring that loot as tribute to three altars, each rolling one offering a week.'),
      k('Brand your prey, gild your offerings, and gamble on what lies under the veil.'),
    ],
    nodes: WILDHEART_NODES,
  },
  {
    key: 'alchemist',
    title: k('Alchemist'),
    tagline: k('Two souls quarrel in every flask.'),
    accent: '#c9a0e0',
    summary: [
      k('Defeated minions yield humours by element.'),
      k('Brew potions for temporary boons: more gold, more foils, richer boosters.'),
      k('Crafted at the alembic between matches.'),
    ],
    ornament: '/ornaments/viking-style2d-007.svg',
    majorOrnament: '/ornaments/path-transmuter.svg',
    nodes: ALCHEMIST_NODES,
  },
  {
    key: 'battlemage',
    title: k('Battlemage'),
    tagline: k('Fury is my fire and the battlefield my anvil.'),
    accent: '#e0736a',
    summary: [
      k('Damage dealt to the enemy avatar builds Ferocity.'),
      k('Win streaks stack Rush of Battle for growing match rewards.'),
      k('Spend Ferocity to re-roll quests and trial foils.'),
    ],
    ornament: '/ornaments/viking-style2d-007.svg',
    majorOrnament: '/ornaments/path-spellcraft.svg',
    nodes: BATTLEMAGE_NODES,
  },
  {
    key: 'oracle',
    title: k('Oracle'),
    tagline: k('Being right can be a curse.'),
    accent: '#82b4e6',
    summary: [
      k('Predict your victory before the match for double or half rewards.'),
      k('Choose an Omen to complete during play.'),
      k('Correct predictions store Foresight for re-rolling packs.'),
    ],
    ornament: '/ornaments/viking-style2d-007.svg',
    majorOrnament: '/ornaments/path-scholar.svg',
    nodes: ORACLE_NODES,
  },
  {
    key: 'mimic',
    title: k('Mimic'),
    tagline: k('Borrowed faces owe no debts.'),
    accent: '#e8c87a',
    summary: [
      k('Replaying a card you already played this game earns Mimicry.'),
      k("Spend it to copy a card your opponent played against you."),
      k('Open two boosters and keep the better one.'),
    ],
    // Two faces sharing one line, the same shape seen twice.
    sigil: {
      points: [[380, 120], [620, 120], [300, 300], [700, 300], [380, 480], [620, 480], [500, 200], [500, 400], [440, 300], [560, 300]],
      edges: [[0, 2], [2, 4], [1, 3], [3, 5], [0, 6], [1, 6], [4, 7], [5, 7], [6, 8], [6, 9], [8, 7], [9, 7]],
    },
    nodes: null,
  },
  {
    key: 'realmwalker',
    title: k('Realm Walker'),
    tagline: k('Every road is a wager.'),
    accent: '#c9a86a',
    summary: [
      k('Fill your satchel in the arena, then set out.'),
      k('Walk the realms, hire a retinue, break the warlord.'),
      k('Fall, and set out again with what the tree remembers.'),
    ],
    art: '/ornaments/viking-ringerike-004.svg',
    ornament: '/ornaments/viking-style2d-007.svg',
    majorOrnament: '/ornaments/path-hunter.svg',
    nodes: REALMWALKER_NODES,
  },
];

export const TREE_WIDTH = 1000;
export const TREE_HEIGHT = 600;

export function getClass(classKey) {
  return CLASSES.find((c) => c.key === classKey) || null;
}

export function isClassAvailable(classKey) {
  return Boolean(getClass(classKey)?.nodes);
}

export function getClassNodes(classKey) {
  return getClass(classKey)?.nodes || [];
}

export function getNode(classKey, nodeKey) {
  return getClassNodes(classKey).find((n) => n.key === nodeKey) || null;
}
